import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errMsg, fmt, ts } from "../lib/api";
import { Panel, PageHead, Spinner, Stat, Btn, Empty } from "../components/common";
import { Switch } from "../components/ui/switch";

const MARKETS = ["ALL", "BURSA", "US", "HK", "SG"];

function Row({ r }) {
  return (
    <tr data-testid={`buyable-row-${r.symbol}`} className={r.buyable ? "" : "opacity-60"}>
      <td className="mono text-slate-900">{r.symbol}<div className="text-[10px] text-slate-500">{r.name}</div></td>
      <td className="text-slate-600">{r.exchange}</td>
      <td className="mono">{r.price ?? "—"} <span className="text-[10px] text-slate-500">{r.currency}</span></td>
      <td className="mono">{r.lot}</td>
      <td className="mono">{r.lot_cost != null ? fmt(r.lot_cost, 2) : "—"}</td>
      <td className="mono">{r.max_cash_buy ?? "—"}</td>
      <td><span data-testid={`buyable-lots-${r.symbol}`} className={`mono rounded-sm px-1.5 py-0.5 text-[11px] font-bold ${r.buyable ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>{r.lots} lot{r.lots === 1 ? "" : "s"}</span></td>
      <td className="text-[11px] text-slate-600">{r.buyable ? <span className="text-emerald-600">{r.via ? <>Can buy <span className="text-amber-600">via RM collateral (margin loan, interest)</span></> : "Can buy"}</span> : r.reason}</td>
    </tr>
  );
}

function Policy() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (body, msg, el) => {
    try { const r = await api.put("/settings", body); setS(r.data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); if (el) el.value = el.defaultValue; }
  };
  if (!s) return null;
  const fx = s.fx_collateral_markets || {};
  return (
    <Panel title="RM collateral policy & BUY alerts" className="mb-3" testid="collateral-policy">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {["US", "HK", "SG"].map((x) => (
          <label key={x} className="flex items-center gap-2 text-xs text-slate-700">
            <Switch checked={!!fx[x]} onCheckedChange={(v) => save({ fx_collateral_markets: { [x]: v } }, `${x}: ${v ? "RM collateral ON" : "cash only"}`)} data-testid={`collateral-switch-${x.toLowerCase()}`} />
            <span className="mono font-semibold">{x}</span>
            <span data-testid={`collateral-state-${x.toLowerCase()}`} className={fx[x] ? "text-amber-600" : "text-emerald-600"}>{fx[x] ? "RM collateral (margin loan, interest)" : "Cash only"}</span>
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <Switch checked={!!s.notify_buy_opportunity} onCheckedChange={(v) => save({ notify_buy_opportunity: v }, v ? "BUY alert emails ON" : "BUY alert emails OFF")} data-testid="buy-alert-switch" />
          Email me each new qualified BUY I can afford now
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-700" data-testid="margin-rates">
        <span>Margin interest estimate (% a year, check your rate in the moomoo app):</span>
        {["USD", "HKD", "SGD"].map((c) => (
          <label key={c} className="flex items-center gap-1"><span className="mono">{c}</span>
            <input key={`${c}-${(s.margin_rates_pct || {})[c]}`} data-testid={`margin-rate-${c.toLowerCase()}`} inputMode="decimal" defaultValue={(s.margin_rates_pct || {})[c]}
              onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== (s.margin_rates_pct || {})[c]) save({ margin_rates_pct: { [c]: v } }, `${c} margin rate ${v}%`, e.target); }}
              className="mono w-16 rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1 text-xs" /></label>
        ))}
      </div>
      <p className="mt-2 text-[10px] text-slate-500">ON: if you hold no USD/HKD/SGD, Moomoo lends it against your RM (daily interest); DMI still caps total exposure at your total cash (1x). OFF: only same-currency cash is used. Press “Check now” after changing to refresh the table.</p>
    </Panel>
  );
}

export default function Buyable() {
  const [d, setD] = useState(null);
  const [m, setM] = useState("ALL");
  const [only, setOnly] = useState(false);
  const load = async () => { try { const r = await api.get("/live/buyable"); setD(r.data); return r.data; } catch (e) { toast.error(errMsg(e)); return null; } };
  useEffect(() => {
    let t;
    const tick = async () => { const x = await load(); if (x?.status === "running") t = setTimeout(tick, 4000); };
    tick();
    return () => clearTimeout(t);
  }, []);
  const refresh = async () => {
    try { const r = await api.post("/live/buyable/refresh"); setD(r.data); const poll = async () => { const x = await load(); if (x?.status === "running") setTimeout(poll, 4000); else if (x?.status === "done") toast.success("Check complete"); else toast.error(`Check ${x?.status}${x?.error ? `: ${x.error}` : ""}`); }; setTimeout(poll, 4000); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Spinner />;
  const running = d.status === "running";
  const rows = d.rows.filter((r) => (m === "ALL" || r.exchange === m) && (!only || r.buyable));
  return (
    <div data-testid="buyable-page">
      <PageHead title="Can Buy Now (LIVE)" sub="Read-only check with Moomoo MY: the maximum shares your cash can buy. RM collateral for US/HK/SG follows the switches below. No orders are placed."
        right={<Btn onClick={refresh} disabled={running} data-testid="buyable-refresh-btn"><RefreshCw className={`h-3.5 w-3.5 ${running ? "animate-spin" : ""}`} />{running ? `Checking ${d.rows.length}/${d.total || "…"}` : "Check now"}</Btn>} />
      <div className="mb-3 rounded-sm border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-700" data-testid="buyable-live-note">This page reads your REAL Moomoo MY account even while the app runs in DEMO mode. Nothing is bought here.</div>
      <Policy />
      {d.status === "interrupted" && <div className="mb-3 text-[11px] text-amber-600" data-testid="buyable-interrupted">The last check was interrupted. Press “Check now” to run it again.</div>}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {["MYR", "USD", "HKD", "SGD"].map((c) => <Stat key={c} testid={`buyable-cash-${c.toLowerCase()}`} label={`${c} cash`} value={d.cash?.[c] != null ? fmt(d.cash[c], 2) : "—"} />)}
        <Stat testid="buyable-count" label="Shares you can buy" value={`${d.summary.buyable} / ${d.summary.checked}`} sub={`Bursa ${d.summary.by_market.BURSA} · US ${d.summary.by_market.US} · HK ${d.summary.by_market.HK} · SG ${d.summary.by_market.SG ?? 0}`} />
        <Stat label="Last checked" value={d.finished_at ? ts(d.finished_at) : running ? "running…" : "never"} />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex gap-1">{MARKETS.map((x) => <button key={x} data-testid={`buyable-market-${x.toLowerCase()}`} onClick={() => setM(x)} className={`mono rounded-sm border px-3 py-1 text-[11px] ${m === x ? "border-blue-500 bg-blue-600 text-white" : "border-[#E2E8F0] text-slate-600 hover:bg-[#F1F5F9]"}`}>{x}</button>)}</div>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={only} onCheckedChange={setOnly} data-testid="buyable-only-toggle" />Only shares I can buy</label>
      </div>
      <Panel testid="buyable-table">
        {!rows.length ? <Empty>{d.status === "never_run" ? "Press “Check now” to ask Moomoo what your cash can buy." : running ? "Checking with Moomoo…" : "No shares match"}</Empty> : (
          <div className="custom-scrollbar overflow-x-auto">
            <table className="tbl w-full">
              <thead><tr><th>Symbol</th><th>Exch</th><th>Price</th><th>Lot size</th><th>1 lot costs</th><th>Max shares (cash)</th><th>Lots</th><th>Status</th></tr></thead>
              <tbody>{rows.map((r) => <Row key={r.symbol} r={r} />)}</tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="mt-2 text-[10px] text-slate-500">Prices: Bursa/HK/SG from the delayed feed. Moomoo recalculates exact limits (incl. fees) at order time. Converting MYR to USD/HKD/SGD in the moomoo app avoids margin interest.</p>
    </div>
  );
}
